import { Box, Stack, Typography, Button, Grid2 as Grid } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import React, { useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageContext } from "../../context/PageContext";
import LoadingPage from "../../components/LoadingPage";
import { useReadingEmail } from "../../hooks/useReadingEmail";
import { useContacts } from "../../hooks/useContacts";
import { ContactAlert } from "../../components/ContactAlert";
import { History } from "../../components/History";
import { ExternalLink } from "../../components/ExternalLink";
import { Tip } from "../../components/Tip";
import { Footer } from "../../components/Footer";
import { useChasable } from "../../hooks/useChasable";
import { useCustomer } from "../../hooks/useCustomer";
import { useRedirectToActivities } from "../../hooks/useRedirectToActivities";
import { InvoiceInformation } from "../Chasables/SuggestedChasable/InvoiceInformation";
import { ContactInfo } from "./ContactInfo";
import { ContactNotFound } from "./ContactNotFound";
import { ChasablesContext } from "../../context/ChasablesContext";
import { ActionTypeEnum } from "@attelas/shared";
import InvoiceComments from "../../components/InvoiceComments/InvoiceComments";

const ReceivableInvoiceDetail = () => {
  const { chasables } = useContext(ChasablesContext);
  const { customerId, invoiceId } = useParams<{ customerId: string; invoiceId: string }>();
  const { chasable, ...chasableResponse } = useChasable(invoiceId, true);
  const { customer, ...customerResponse } = useCustomer(customerId);
  const { emailAddress } = useReadingEmail();
  const { contacts, ...contactsResponse } = useContacts(emailAddress);
  const isSaved = contacts?.find(({ id }) => id === customerId)?.isSaved ?? false;
  const invoiceNumber = chasable?.invoiceDetails?.invoiceNumber;
  const isNewInvoice =
    chasables?.actions.find((action) => action.id === chasable?.chasableId)?.type === ActionTypeEnum.NewInvoice;
  const isPromiseToPayBroken =
    chasables?.actions.find((action) => action.id === chasable?.chasableId)?.type === ActionTypeEnum.PromiseToPayBroken;

  const { setNewTitle } = useContext(PageContext);

  useEffect(() => {
    setNewTitle(invoiceNumber ? `Receivable ${invoiceNumber}` : "Receivable");
  }, [setNewTitle, invoiceNumber]);

  useRedirectToActivities(emailAddress);

  if (
    chasableResponse.isLoading ||
    contactsResponse.isLoading ||
    customerResponse.isLoading ||
    chasableResponse.isError ||
    contactsResponse.isError ||
    customerResponse.isError
  ) {
    return (
      <WithFooter>
        <LoadingPage />;
      </WithFooter>
    );
  }

  if (!customer) {
    return (
      <WithFooter>
        <ContactNotFound email={emailAddress ?? ""} />;
      </WithFooter>
    );
  }

  if (!chasable) {
    return (
      <WithFooter>
        <Tip title="Invoice not found" />;
      </WithFooter>
    );
  }

  return (
    <WithFooter>
      <Stack spacing={2} pb={8}>
        <Grid size={12}>
          <ContactInfo
            title="Customer"
            contactLink={customer.link}
            companyName={customer.companyName}
            accountNumber={customer.accountNumber}
            status={{ status: chasable.invoiceDetails.status, dueDate: chasable.invoiceDetails.dueDate ?? "" }}
          />
        </Grid>
        {emailAddress && (
          <ContactAlert
            isSaved={isSaved}
            email={emailAddress}
            companyName={customer.companyName}
            contactLink={customer.link}
            integration={customer.integration}
            accountNumber={customer.accountNumber}
          />
        )}
        <Box>
          <Typography variant="h2" sx={{ lineHeight: "18px" }}>
            Invoice
          </Typography>
          <ExternalLink href={chasable.invoiceDetails.link}>{invoiceNumber}</ExternalLink>
        </Box>
        <InvoiceInformation
          invoice={chasable.invoiceDetails}
          companyName={chasable.customerDetails.companyName}
          accountNumber={chasable.customerDetails.accountNumber}
          assignedTo={chasable.assignedTo}
          customerLink={customer.link}
          isNewInvoice={isNewInvoice}
          isPromiseToPayBroken={isPromiseToPayBroken}
          onRefresh={chasableResponse.refetchChasable}
        />
        <InvoiceComments invoiceId={invoiceId} onCommentAdded={chasableResponse.refetchChasable} />
        <History title="History" emptyText="No history" history={chasable.chasableHistory} />
      </Stack>
    </WithFooter>
  );
};

export function WithFooter({ children }: { children: React.ReactNode }) {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const onBackClick = () => {
    navigate(`/receivables/${customerId}`);
  };

  return (
    <>
      {children}
      <Footer>
        <Grid size="grow">
          <Button variant="outlined" fullWidth={true} startIcon={<ArrowBackIcon />} onClick={onBackClick}>
            Back
          </Button>
        </Grid>
      </Footer>
    </>
  );
}

export default ReceivableInvoiceDetail;
